/**
 * Mutual Aid API client.
 *
 * Wraps /api/mutual-aid — peer-to-peer request + exchange tracking for
 * Apóyate SB (Sobrato & Destination Home grants). A REQUEST is someone asking
 * for help (food/housing/medical/...); an EXCHANGE is an actual handoff
 * (giver → receiver) against a request.
 *
 * Backend: app/routers/mutual_aid.py. All calls go through authFetch.
 */

import { authFetchJson } from '@/lib/authFetch';

export type MutualAidRequestType =
  | 'food' | 'housing' | 'medical' | 'transportation' | 'financial' | 'general' | 'other';

export type MutualAidStatus =
  | 'open' | 'in_progress' | 'fulfilled' | 'partially_fulfilled' | 'unfulfilled' | 'closed';

export interface MutualAidRequest {
  id: number;
  cbo_id: number;
  program_id?: number | null;
  requester_member_id?: number | null;
  request_type: MutualAidRequestType;
  channel?: string | null;
  status: MutualAidStatus;
  responder_count: number;
  outcome?: string | null;
  community_quote?: string | null;
  created_date: string;
  created_by?: number | null;
  created_at: string;
  updated_at: string;
}

export interface MutualAidExchange {
  id: number;
  cbo_id: number;
  request_id: number;
  giver_member_id?: number | null;
  receiver_member_id?: number | null;
  item?: string | null;
  handoff_completed: boolean;
  completed_date?: string | null;
  created_at: string;
}

export interface MutualAidRequestCreate {
  request_type?: MutualAidRequestType;
  channel?: string;
  status?: MutualAidStatus;
  program_id?: number;
  requester_member_id?: number;
  responder_count?: number;
  outcome?: string;
  community_quote?: string;
  created_date?: string;
}

function buildQuery(params: Record<string, any> = {}): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

export async function listRequests(
  params: { status?: string; request_type?: string; program_id?: number } = {},
): Promise<MutualAidRequest[]> {
  return authFetchJson<MutualAidRequest[]>(`/api/mutual-aid/requests${buildQuery(params)}`);
}

export async function createRequest(payload: MutualAidRequestCreate): Promise<MutualAidRequest> {
  return authFetchJson<MutualAidRequest>('/api/mutual-aid/requests', { method: 'POST', jsonBody: payload });
}

export async function updateRequest(id: number, payload: Partial<MutualAidRequestCreate>): Promise<MutualAidRequest> {
  return authFetchJson<MutualAidRequest>(`/api/mutual-aid/requests/${id}`, { method: 'PATCH', jsonBody: payload });
}

export async function listExchanges(requestId: number): Promise<MutualAidExchange[]> {
  return authFetchJson<MutualAidExchange[]>(`/api/mutual-aid/requests/${requestId}/exchanges`);
}

export async function createExchange(
  requestId: number,
  payload: { giver_member_id?: number; receiver_member_id?: number; item?: string; handoff_completed?: boolean; completed_date?: string },
): Promise<MutualAidExchange> {
  return authFetchJson<MutualAidExchange>(`/api/mutual-aid/requests/${requestId}/exchanges`, {
    method: 'POST',
    jsonBody: payload,
  });
}

export interface ReportResult {
  columns: string[];
  rows: Record<string, any>[];
}

/** Request/exchange counts by type + status (reuses the mutual_aid_summary report). */
export async function getSummary(programId?: number): Promise<ReportResult> {
  return authFetchJson<ReportResult>(`/api/mutual-aid/summary${buildQuery({ program_id: programId })}`);
}

/** De-duplicated pod participant counts (a person in multiple pods counts once). */
export async function getPodParticipants(podFieldId?: number): Promise<ReportResult> {
  return authFetchJson<ReportResult>(`/api/mutual-aid/pod-participants${buildQuery({ pod_field_id: podFieldId })}`);
}
